import { React, useState, useContext, useEffect, useRef } from 'react';
import { AuthContext } from '../../services/auth.service';
import { CompanyLogo } from '../../components/companyLogo';
import { Alert } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';
import '../../styles/auth.scss';

export const PasswordReset = () => {
    const { resetPassword } = useContext(AuthContext);

    const [email, setEmail] = useState('');
    const [showAlert, setShowAlert] = useState(false);
    const [alertData, setAlertData] = useState({});
    const [resendCooldown, setResendCooldown] = useState(0);
    const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);
    const [captchaToken, setCaptchaToken] = useState('');
    const recaptchaRef = useRef(null);

    // Add countdown timer effect
    useEffect(() => {
        let timer;
        if (resendCooldown > 0) {
            timer = setInterval(() => {
                setResendCooldown(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [resendCooldown]);

    const handleRecaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const submitBtn = document.getElementById("submitBtn");

        if (!captchaToken) {
            setAlertData({
                type: 'error',
                message: 'Captcha Required',
                description: 'Please complete the reCAPTCHA before submitting.',
            });
            setShowAlert(true);
            return;
        }

        const response = await resetPassword({ email, captchaToken });

        if (response?.result) {
            setAlertData({
                type: 'success',
                message: 'Reset Link Sent',
                description: 'We have sent you a reset password link. If you have not received it, please check your spam folder. Otherwise, contact us if you do not receive it in a few minutes.',
            });
            setShowAlert(true);
            submitBtn.disabled = true;
            recaptchaRef.current?.reset(); // Reset reCAPTCHA after successful submission
            setCaptchaToken('');
        } else if (response?.remainingTime) {
            setResendCooldown(response.remainingTime);
            setAlertData({
                type: 'error',
                message: 'Please wait',
                description: `Please wait ${response.remainingTime} seconds before requesting another password reset.`,
            });
            setShowAlert(true);
        } else if (response?.maxAttemptsReached) {
            setMaxAttemptsReached(true);
            setAlertData({
                type: 'error',
                message: 'Maximum attempts reached',
                description: 'Maximum attempts reached (5 per hour). Please try again later or contact support.',
            });
            setShowAlert(true);
        } else {
            setAlertData({
                type: 'error',
                message: 'Error',
                description: response?.msg || 'An error occurred. Please try again.',
            });
            setShowAlert(true);
            recaptchaRef.current?.reset(); // Reset reCAPTCHA on error
            setCaptchaToken('');
        }
    };

    const handleChange = (e) => {
        setEmail(e.target.value);
    };

    const renderSubmitButton = () => {
        if (maxAttemptsReached) {
            return (
                <button 
                    className="auth-button"
                    disabled={true}
                >
                    Maximum attempts reached
                </button>
            );
        }

        if (resendCooldown > 0) {
            return (
                <button 
                    className="auth-button"
                    disabled={true}
                >
                    Try again in {resendCooldown}s
                </button>
            );
        }

        return (
            <button 
                id='submitBtn'
                type="submit" 
                className="auth-button"
                disabled={!captchaToken}
            >
                Reset Password
            </button>
        );
    };
    
    return (
        <div className='auth-container reset-password-request-page'>
            <div className="bottom-circle"></div>
            <div className="auth-form-container">
                <div className="text-center mb-4">
                    <CompanyLogo />
                </div>

                <h1 className="auth-heading text-center">Forgot your password?</h1>
                <p className="auth-text text-center">
                    Please enter your email and we will send you
                    the link to reset your password. 
                </p>

                {showAlert && 
                    <Alert  
                        message={alertData.message}
                        description={alertData.description}
                        type={alertData.type}
                        showIcon
                        className="auth-alert"
                    />
                }

                <form method="POST" className="mt-4" onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <input
                            type="text"
                            autoFocus
                            autoCapitalize="none"
                            autoComplete="email"
                            maxLength="150"
                            className="auth-input"
                            required
                            id="id_auth-email"
                            placeholder="Email address"
                            name="email"
                            onChange={handleChange}
                            value={email}
                        />
                    </div>
                    <div className="mt-3">
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey="6Ldi_i4rAAAAAFCkkEM4jMWTrnKbVZ4OggTzxKiV"
                            onChange={handleRecaptchaChange}
                        />
                    </div>
                    <div className="mt-4">
                        {renderSubmitButton()}
                    </div>
                </form>
            </div>
        </div>
    );
};